import { useEffect, useState } from "react";

import {
  Box,
  FormControlLabel,
  FormControl,
  Grid,
  TextField,
  Checkbox,
  FormHelperText,
  Typography,
  Divider,
  InputLabel,
  Select,
  Button
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { getIntegerFromString } from "./UtilityFunctions";
import { postNotification } from "./Notifications";
import ImageUploader from "./ImageUploader";

// Deliberately not state objects
let updateStack = [];
let updating = false;

const useStyles = makeStyles((theme) => ({
  formControl: {
    display: "flex",
    justifyContent: "center",
    minWidth: 120,
    "& .MuiSelect-select:focus": {
      backgroundColor: "unset"
    }
  },
  formControlReadOnly: {
    color: `${theme?.palette?.text?.disabled} !important`,
    borderColor: "rgba(0, 0, 0, 0.8) !important",
    "& .MuiInputBase-formControl": {
      color: `${theme?.palette?.text?.disabled} !important`
    },
    "& .MuiFormLabel-root": {
      color: `${theme?.palette?.text?.disabled} !important`
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme?.palette?.text?.disabled} !important`
    }
  },
  formControlDense: {
    "& legend": {
      display: "none"
    }
  }
}));

const flatternFormSchema = async (scorecard, record) => {
  let _formRows = [];
  let _formulas = [];
  let _updateFormulaFields = {};
  let _formData = {};
  let _masterLists = {};
  let _requiredFields = {};
  let _groups = {};
  let _selectOptions = {};
  let _preSelectedEvents = {};
  let _formCols = {};
  let _bcpUpdateCols = [];
  let _radioGroups = {};
  let _galleries = [];

  if (scorecard?.lists?.length > 0) {
    scorecard.lists.forEach((list) => {
      if (!list.preSorted && list.list) {
        list.list.sort(compGroupThenName);
      }

      let lkey = "";
      if (list.collection) {
        if (list.collection === "Player") {
          list.list = [];

          if (list.inclTie) {
            list.list.push({
              name: "Tie",
              value: 0
            });
          }

          let i = 1;
          while (record?.["player" + i]) {
            list.list.push({
              name: getPlayerName(
                record["player" + i],
                `${
                  record.pairingTable === "TeamPairing" ? "Team" : "Player"
                } ${i}`
              ),
              value: i
            });
            i++;
          }
        }

        if (list.id) {
          lkey = list.id;
        } else {
          lkey = list.collection;
        }
      } else if (list.scorecardListId) {
        lkey = "ScorecardList$" + list.scorecardListId;
      }
      if (lkey !== "") {
        _masterLists[lkey] = list.list;
      }
    });
  }

  if (scorecard?.template?.rows) {
    if (record?.event?.adepticonTeamEvent) {
      scorecard.template.rows.forEach((row, rowIndex) => {
        // every row should have them
        if (row.cols) {
          row.cols.forEach((col, colIndex) => {
            if (col.id) {
              if (col.formula === "$(p1-firstName) & ' ' & $(p1-lastName)") {
                var secondaryRow = {
                  cols: [
                    {
                      id: "lbl-player1-secondary",
                      type: "label",
                      text: `${
                        record?.secondaryPairing?.player1?.firstName || ""
                      } ${
                        record?.secondaryPairing?.player1?.lastName || ""
                      }`.trim()
                    },
                    {
                      id: "lbl-player2-secondary",
                      type: "label",
                      text: `${
                        record?.secondaryPairing?.player2?.firstName || ""
                      } ${
                        record?.secondaryPairing?.player2?.lastName || ""
                      }`.trim()
                    }
                  ],
                  secondaryPlayerRow: true
                };

                scorecard.template.rows.splice(rowIndex + 1, 0, secondaryRow);
              }
            }
          });
        }
      });
    }

    scorecard.template.rows.forEach((row, rowIndex) => {
      // every row should have them
      if (row.cols) {
        let label = undefined;
        let _row = [];

        row.cols.forEach((col, colIndex) => {
          if (col.id) {
            if (!label && col.type === "label") label = col.text;
            if (col.bcpfield) {
              col.bcpfield = col.bcpfield.replace(".game.", "Game.");

              if (col.bcpfield.endsWith(".army")) {
                col.bcpfield = col.bcpfield.replace(".army", ".armyName");
              }
            }

            if (col.bcpfield && col.bcpupdate) _bcpUpdateCols.push(col);

            if (col.subType === "checkbox") {
              col.type = "checkbox";
            }

            if (
              col.formula ||
              col.bcpfield ||
              col.type === "input" ||
              col.type === "select" ||
              col.type === "checkbox" ||
              col.type === "radio"
            ) {
              _formData[col.id] = "";
              if (col.required) _requiredFields[col.id] = "Required";
            }

            if (record.metaData?.[col.id]) {
              _formData[col.id] = record.metaData[col.id];
            } else if (col.bcpfield) {
              let bcpfields = col.bcpfield.split(".");
              let value = record;
              for (let i = 0; i < bcpfields.length; i++) {
                if (value[bcpfields[i]]) {
                  value = value[bcpfields[i]];
                } else {
                  value = undefined;
                  break;
                }
              }
              if (value) {
                _formData[col.id] = value;
              }
            } else if (col.defaultValue !== undefined) {
              _formData[col.id] = col.defaultValue;
            }

            if (col.formula) {
              _formulas.push(col);

              let fomulaFieldString = `${col.formula}`;
              let regEx = /\w*\$\([^\s]*/;
              let result;
              while ((result = fomulaFieldString.match(regEx)) !== null) {
                let fieldId = result[0].replace(/\$\(/, "").replace(/\)/, "");
                fomulaFieldString = fomulaFieldString.split(result[0]).join("");
                _updateFormulaFields[fieldId] = true;
              }
            }

            if (col.type === "select") {
              _selectOptions[col.id] = setSelectOptions(
                [],
                { ...col, masterLists: _masterLists },
                true
              );
            }

            if (col.groupId && col.groupType) {
              if (!_groups[col.groupId])
                _groups[col.groupId] = { id: col.groupId, type: col.groupType };
              if (col.groupMessage)
                _groups[col.groupId].message = col.groupMessage;
              if (col.groupType === "unique") {
                if (!_groups[col.groupId].uniqueFields)
                  _groups[col.groupId].uniqueFields = [];
                _groups[col.groupId].uniqueFields.push(col.id);
              }
              // don't think we are using this
              /*if (col.uniqueFields) {
                _groups[col.groupId].uniqueFields = col.uniqueFields;
              }*/
            }

            let updateIds = col.updateId ? [col.updateId] : col.updateIds;

            if (
              updateIds &&
              _formData[col.id] &&
              _formData[col.id] !== "" &&
              _selectOptions[col.id]
            ) {
              updateIds.forEach((updateId) => {
                let selOpt;
                for (let og = 0; og < _selectOptions[col.id].length; og++) {
                  if (
                    _selectOptions[col.id][og].value !== undefined &&
                    _selectOptions[col.id][og].value === _formData[col.id]
                  ) {
                    selOpt = _selectOptions[col.id][og];
                    break;
                  }
                }
                if (selOpt) {
                  if (!_preSelectedEvents[updateId]) {
                    _preSelectedEvents[updateId] = [];
                  }
                  _preSelectedEvents[updateId].push({
                    type: col.updateType || "replace",
                    obj: selOpt
                  });
                }
              });
            }

            // handle dynamic hidden
            if (row.hidden?.bcpfield) {
              let bcpfields = row.hidden.bcpfield.split(".");
              let value = record;
              for (let i = 0; i < bcpfields.length; i++) {
                if (value[bcpfields[i]]) {
                  value = value[bcpfields[i]];
                } else {
                  value = undefined;
                  break;
                }
              }

              row.hidden = row.hidden.inverse ? !value : value;
            }

            // handle components
            if (!row.hidden) {
              if (
                col.type === "header" ||
                col.type === "line" ||
                col.type === "spacer"
              ) {
                _row.push(col);
              } else if (
                col.type === "label" /*&&
                (row.cols.length === 1 || colIndex > 0)*/
              ) {
                _row.push(col);
              } else if (col.type === "select") {
                if (col.label === undefined) {
                  if (label) col.label = label;
                  else if (col.placeholder) col.label = col.placeholder;
                }
                _row.push(col);
              } else if (col.type === "input") {
                if (col.label === undefined) {
                  if (label) col.label = label;
                  else if (col.placeholder) col.label = col.placeholder;
                }
                _row.push(col);
              } else if (col.type === "checkbox") {
                if (col.label === undefined) {
                  if (label) col.label = label;
                  else if (col.placeholder) col.label = col.placeholder;
                }
                _row.push(col);
              } else if (col.type === "radio") {
                if (col.label === undefined) {
                  if (label) col.label = label;
                  else if (col.placeholder) col.label = col.placeholder;
                }
                if (col.name) {
                  if (!_radioGroups[col.name]) _radioGroups[col.name] = [];
                  _radioGroups[col.name].push(col.id);
                }
                _row.push(col);
              } else if (col.type === "gallery") {
                _galleries.push(col);
                _row.push(col);
              }
            }
            _formCols[col.id] = col;
          }
        });

        if (!row.hidden) _formRows.push(_row);
      }
    });

    _formRows.forEach((row, rowIndex) => {
      row.forEach((col, colIndex) => {
        if (col.id && _updateFormulaFields[col.id]) {
          col.updateFormulas = true;
        }
      });
    });
    _formData = updateFormulas(_formulas, _formData);
  }

  return {
    rows: _formRows,
    cols: _formCols,
    initialValues: _formData,
    requiredFields: _requiredFields,
    masterLists: _masterLists,
    formulas: _formulas,
    groups: _groups,
    selectOptions: _selectOptions,
    preSelectedEvents: _preSelectedEvents,
    scorecard: scorecard,
    bcpUpdateCols: _bcpUpdateCols,
    radioGroups: _radioGroups,
    galleries: _galleries
  };
};

export function updateFormulas(cols, formData) {
  if (!cols || !formData) return formData;

  cols.forEach((col) => {
    formData[col.id] = doFormula(col, formData);
  });

  return formData;
}

function rank(num, arr, asc) {
  return (
    arr
      .sort(function (a, b) {
        if (asc) return a - b;
        else return b - a;
      })
      .indexOf(num) + 1
  );
}

export function doFormula(col, formData) {
  if (!col.formula || !formData) return;

  let formula = `${col.formula}`;
  let regEx = /\w*\$\([^\s]*/;
  let result;

  let rankFormula = formula.startsWith("rank");
  let rankDirection = rankFormula ? formula.split(" ")?.[1] : undefined;

  while ((result = formula.match(regEx)) !== null) {
    let fieldId = result[0].replace(/\$\(/, "").replace(/\)/, "");
    formula = formula.split(result[0]).join(
      col.subType === "number"
        ? formData[fieldId] ||
            // eslint-disable-next-line
            (rankDirection === "asc" && formData[fieldId] != 0 ? 99999999 : 0)
        : typeof formData[fieldId] === "string"
          ? formData[fieldId]?.split(" ").join("&nbsp;") || ""
          : formData[fieldId] ||
            // eslint-disable-next-line
            (rankDirection === "asc" && formData[fieldId] != 0 ? 99999999 : 0)
    );
  }

  let val = 0;
  if (rankFormula) {
    // rank function
    let steps = formula.split(" ");
    if (steps?.length > 3) {
      let rankValue = Number(steps[2] || 0);
      let list = steps.slice(3);
      if (list?.length > 0) {
        list = list.map((el) => Number(el));
        val = rank(rankValue, list, rankDirection === "asc");
      }
    }
  } else if (formula.startsWith("count")) {
    // count function
    let steps = formula.split(" ");
    if (steps?.length > 3) {
      let countValue = steps[1] || 0;
      let list = steps.slice(2);
      if (list?.length > 0) {
        for (let item of list) {
          // eslint-disable-next-line
          if (item == countValue) {
            val++;
          }
        }
      }
    }
  } else if (formula.search(/\?/) >= 0) {
    // conditional
    let steps = formula.split(" ");
    for (let i = 0; i < steps.length; i++) {
      let val1 = steps[i];
      if (!isNaN(val1)) {
        val1 = parseFloat(val1);
      }
      i++;
      let operator = steps[i];
      i++;
      let val2 = steps[i];
      if (!isNaN(val2)) {
        val2 = parseFloat(val2);
      }
      i++; // ?

      if (doFormulaStep(val1, operator, val2)) {
        i++; // true val
        val = steps[i];
        break;
      } else {
        i++; // true val
        i++; // :
        if (i === steps.length - 2) {
          i++; // true val
          val = steps[i];
          break;
        }
      }
    }
    if (typeof val === "string") {
      val = val.replace(/&nbsp;/g, " ");
    }
  } else if (formula.search(/&/) >= 0) {
    // text
    formula = formula.replace("' '", "&nbsp;");
    let steps = formula.split(" ");
    val = steps[0];
    for (let i = 1; i < steps.length; i++) {
      let operator = steps[i];
      i++;
      let val2 = steps[i];
      val = doFormulaStep(val, operator, val2);
    }
    val = val ? val.replace(/&nbsp;/g, " ").trim() : null;
  } else {
    // mathematical
    let steps = formula.split(" ");
    val = Number(steps[0]);
    for (let i = 1; i < steps.length; i++) {
      let operator = steps[i];
      i++;
      let val2 = Number(steps[i]);
      val = doFormulaStep(val, operator, val2);
    }
  }
  return val;
}

function doFormulaStep(post, operator, value) {
  switch (operator) {
    case ">":
      return post > value;
    case "<":
      return post < value;
    case ">=":
      return post >= value;
    case "<=":
      return post <= value;
    case "==":
      // eslint-disable-next-line
      return post == value;
    case "!=":
      // eslint-disable-next-line
      return post != value;
    case "===":
      return post === value;
    case "!==":
      return post !== value;
    case "+":
      return post + value;
    case "&":
      return post + value;
    case "-":
      return post - value;
    case "/":
      return post / value;
    case "*":
      return post * value;
    default:
      return false;
  }
}

export function BCPScorecardForm(props) {
  const {
    schema,
    record,
    readOnly,
    setValuesDirty,
    submitForm,
    values,
    setValues
  } = props;

  const {
    cols,
    rows,
    masterLists,
    formulas,
    initialValues,
    requiredFields,
    groups,
    preSelectedEvents,
    scorecard,
    radioGroups
  } = schema;

  const [errors, setErrors] = useState({});
  const [allSelectOptions, setAllSelectOptions] = useState({});

  const handleChange = (e, col) => {
    if (!e) return;

    let newValues = {
      ...values
    };

    let update = false;
    if (e.target.type === "checkbox") {
      let newValue = e.target.checked ? JSON.parse(col.value) : "";
      if (!values[e.target.name] || values[e.target.name] !== newValue) {
        update = true;
        newValues[e.target.name] = newValue;

        if (col.name && radioGroups[col.name]) {
          radioGroups[col.name].forEach((otherCols) => {
            if (otherCols !== col.id) {
              newValues[otherCols] = "";
            }
          });
        }
      }
    } else if (
      !values[e.target.name] ||
      values[e.target.name] !== e.target.value
    ) {
      update = true;
      newValues[e.target.name] = e.target.value;
    }

    // handle change to field
    if (update) {
      if (col.updateFormulas) {
        newValues = updateFormulas(formulas, newValues);
      }

      /* 
        handle group functionality
        - primarily unique values
        - primarily select/list fields
      */
      if (col.groupId && groups[col.groupId]) {
        if (groups[col.groupId].uniqueFields) {
          for (
            let p1 = 0;
            p1 < groups[col.groupId].uniqueFields.length - 1;
            p1++
          ) {
            for (
              let p2 = p1 + 1;
              p2 < groups[col.groupId].uniqueFields.length;
              p2++
            ) {
              if (
                newValues[groups[col.groupId].uniqueFields[p1]] &&
                newValues[groups[col.groupId].uniqueFields[p2]] &&
                newValues[groups[col.groupId].uniqueFields[p1]] ===
                  newValues[groups[col.groupId].uniqueFields[p2]]
              ) {
                postNotification(
                  "error",
                  `You have a duplicate ${
                    newValues[groups[col.groupId].uniqueFields[p1]]
                  }${
                    groups[col.groupId].message
                      ? ` for ${groups[col.groupId].message}`
                      : ""
                  }`
                );
              }
            }
          }
        }
      }

      /*
          handle fields updating other fields
          - primarily select/list fields
              eg. Warhammer 40,000 Secondaries have different value options
      */
      let updateIds = col.updateId ? [col.updateId] : col.updateIds;
      if (updateIds) {
        updateIds.forEach((updateId) => {
          if (allSelectOptions[updateId] && col.updateType) {
            if (e.target.selectedIndex && e.target[e.target.selectedIndex]) {
              let masterListId =
                e.target[e.target.selectedIndex].getAttribute(
                  "data-masterlist"
                );
              let masterListIndex = e.target[
                e.target.selectedIndex
              ].getAttribute("data-masterlistindex");
              if (
                masterListId &&
                masterLists[masterListId] &&
                masterListIndex &&
                masterLists[masterListId][masterListIndex]
              ) {
                setAllSelectOptions({
                  ...allSelectOptions,
                  [updateId]: setSelectOptions(
                    allSelectOptions[updateId],
                    {
                      ...col,
                      ...masterLists[masterListId][masterListIndex],
                      masterLists: masterLists
                    },
                    col.updateType === "replace"
                  )
                });
              }
            } else {
              // reset to initial options
              setAllSelectOptions({
                ...allSelectOptions,
                [updateId]: schema.selectOptions[updateId]
              });
            }
          }
        });
      }

      setValuesDirty(true);

      // handle required fields
      if (requiredFields[e.target.name]) {
        if (!e.target.value || e.target.value === "") {
          setErrors({
            ...errors,
            [e.target.name]: "Required"
          });
        } else if (errors[e.target.name]) {
          let errs = { ...errors };
          delete errs[e.target.name];
          setErrors(errs);
        }
      }

      setValues(newValues);

      if (scorecard.isLiveUpdate) {
        updateStack.push({
          values: newValues,
          isDone: false
        });
        doUpdates();
      }
    }

    return;
  };

  const validateRequired = (validateForm, updateTouched) => {
    let errs = {};
    if (validateForm) {
      if (requiredFields) {
        Object.keys(requiredFields).forEach((key) => {
          if (!validateForm[key] || validateForm[key] === "") {
            errs[key] = "Required";
          }
        });
      }
      if (updateTouched) {
        Object.keys(validateForm).forEach((key) => {
          // setFieldTouched(key);
        });
      }
    }

    setErrors(errs);
    return errs;
  };

  useEffect(() => {
    const check = async () => {
      setValues(initialValues ? { ...initialValues } : {});
      validateRequired(initialValues, false);

      updating = false;
      updateStack = [];

      let aso = schema.selectOptions ? { ...schema.selectOptions } : {};
      if (preSelectedEvents) {
        Object.keys(preSelectedEvents).forEach((eventKey) => {
          if (cols[eventKey]) {
            preSelectedEvents[eventKey].forEach((preSelectedEvent) => {
              if (preSelectedEvent.obj) {
                if (preSelectedEvent.type === "replace") {
                  aso[eventKey] = setSelectOptions(
                    allSelectOptions[eventKey],
                    {
                      ...cols[eventKey],
                      ...masterLists[preSelectedEvent.obj.masterList][
                        preSelectedEvent.obj.masterListIndex
                      ],
                      masterLists: masterLists
                    },
                    preSelectedEvent.type === "replace"
                  );
                } else if (preSelectedEvent.type === "linkedList") {
                  if (cols[eventKey].linkedField?.linkChild === "group") {
                    aso[eventKey] = aso[eventKey].filter((opt) => {
                      return opt.group === preSelectedEvent.obj?.value;
                    });
                  }
                }
              }
            });
          }
        });
      }
      setAllSelectOptions(aso);
    };
    check();
    // eslint-disable-next-line
  }, [schema, record]);

  const doUpdates = async () => {
    if (!updating && updateStack.length > 0) {
      updating = true;
      let update;
      while (updateStack.length > 0) {
        update = updateStack.shift();
      }

      if (update) {
        await submitForm(update.values, update.isDone, false);
        updating = false;
        await doUpdates();
      } else {
        updating = false;
      }
    }
  };

  return (
    <>
      {rows &&
        rows.map((row, rowIndex) => {
          return (
            <Grid container spacing={3} key={rowIndex} alignItems="center">
              <RowContents
                row={row}
                values={values}
                readOnly={readOnly}
                handleChange={handleChange}
                masterLists={masterLists}
                allSelectOptions={allSelectOptions}
              />
            </Grid>
          );
        })}
    </>
  );
}

const RowContents = ({
  row,
  values,
  readOnly,
  handleChange,
  masterLists,
  allSelectOptions
}) => {
  return (
    <>
      {row.map((col, colIndex) => {
        let sx = {};
        if (row.length > 1 && col.class && col.class.startsWith("col-")) {
          let colWidth = getIntegerFromString(col.class.replace("col-", ""));
          if (colWidth) {
            sx.minWidth = `${(colWidth / 12) * 100}%`;
            sx.maxWidth = `${(colWidth / 12) * 100}%`;
          }
        }

        return (
          <Grid item xs sx={sx} key={colIndex}>
            {col.type === "header" ? (
              <BCPFormHeader {...col} value={values[col.id]} />
            ) : col.type === "label" ? (
              <BCPFormLabel {...col} value={values[col.id]} />
            ) : col.type === "radio" ||
              (col.type === "input" && col.subType === "radio") ? (
              <BCPFormRadio
                {...col}
                readonly={
                  readOnly
                    ? true
                    : col.readonly !== undefined
                      ? col.readonly
                      : false
                }
                value={col.value}
                checked={values[col.id] === JSON.parse(col.value)}
                handleChange={(e) => {
                  handleChange(e, col);
                }}
              />
            ) : col.type === "input" ? (
              <BCPFormInput
                {...col}
                readonly={
                  readOnly
                    ? true
                    : col.readonly !== undefined
                      ? col.readonly
                      : false
                }
                value={values[col.id] === undefined ? "" : values[col.id]}
                handleChange={(e) => {
                  handleChange(e, col);
                }}
              />
            ) : col.type === "checkbox" ? (
              <BCPFormCheckbox
                {...col}
                readonly={
                  readOnly
                    ? true
                    : col.readonly !== undefined
                      ? col.readonly
                      : false
                }
                value={col.value}
                // eslint-disable-next-line
                checked={values[col.id] == JSON.parse(col.value)}
                handleChange={(e) => {
                  handleChange(e, col);
                }}
              />
            ) : col.type === "select" ? (
              <BCPFormSelect
                {...col}
                readonly={
                  readOnly
                    ? true
                    : col.readonly !== undefined
                      ? col.readonly
                      : false
                }
                value={values[col.id] === undefined ? "" : values[col.id]}
                masterLists={masterLists}
                handleChange={(e) => {
                  handleChange(e, col);
                }}
                options={allSelectOptions[col.id]}
              />
            ) : col.type === "line" ? (
              <BCPFormLine {...col} />
            ) : col.type === "gallery" ? (
              <BCPFormGallery
                {...col}
                readonly={
                  readOnly
                    ? true
                    : col.readonly !== undefined
                      ? col.readonly
                      : false
                }
                values={values[col.id]}
                handleChange={(e) => {
                  handleChange(e, col);
                }}
              />
            ) : (
              <></>
            )}
          </Grid>
        );
      })}
    </>
  );
};

function BCPFormGallery(props) {
  const {
    id,
    label,
    values,
    handleChange,
    readonly
    // minImages
  } = props;
  const maxImages = props?.maxImages || 3;
  const [imageArray, setImageArray] = useState(values || []);
  const setFieldValue = (index, fieldName, imageData) => {
    let newValues = [...imageArray];
    newValues[index] = imageData;
    setImageArray(newValues);

    let e = {
      target: {
        name: id,
        value: newValues
      }
    };
    handleChange(e);
  };

  useEffect(() => {
    setImageArray(values || []);
  }, [values]);

  return (
    <Grid container>
      {imageArray?.length > 0 ? (
        imageArray.map((_image, index) => (
          <Grid item xs={12}>
            <ImageUploader
              label={label}
              name={`${index}`}
              values={imageArray}
              fileId={null}
              setFieldValue={(fieldName, imageData) => {
                setFieldValue(index, fieldName, imageData);
              }}
              noAspect
              disabled={readonly}
            />
          </Grid>
        ))
      ) : (
        <></>
      )}
      {imageArray?.length < maxImages ? (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={() => setImageArray([...imageArray, ""])}
          >
            Add Image
          </Button>
        </Grid>
      ) : (
        <></>
      )}
    </Grid>
  );
}

function BCPFormHeader(props) {
  const { text, value, style, variant } = props;
  return (
    <Typography variant={variant || "h4"} gutterBottom sx={{ ...style }}>
      {value || text}
    </Typography>
  );
}

function BCPFormLabel(props) {
  const { text, value, style } = props;
  return (
    <Typography variant="body1" gutterBottom sx={{ mt: "32px", ...style }}>
      {value || text}
    </Typography>
  );
}

function BCPFormLine(props) {
  const { style } = props;
  return (
    <Divider
      sx={{
        borderTop: "2px solid #242424",
        ...style
      }}
    />
  );
}

function BCPFormCheckbox(props) {
  const {
    id,
    label,
    readonly,
    textAlign,
    value,
    checked,
    handleChange,
    error,
    touched
  } = props;

  return (
    <Box
      sx={{
        minHeight: 56,
        mt: 1,
        textAlign: textAlign
      }}
    >
      <FormControlLabel
        label={label}
        style={{
          color: Boolean(touched && error) ? "#f44336" : "inherit"
        }}
        control={
          <Checkbox
            name={id}
            value={value}
            checked={checked}
            error={error}
            onChange={handleChange}
            disabled={readonly}
          />
        }
      />
      {Boolean(touched && error) && (
        <FormHelperText error>{error}</FormHelperText>
      )}
    </Box>
  );
}

function BCPFormInput(props) {
  const {
    id,
    subType,
    min,
    max,
    label,
    value,
    handleChange,
    required,
    readonly,
    error,
    helperText,
    multiline,
    rows
  } = props;

  let readOnlyProps = readonly
    ? {
        disabled: true,
        inputProps: { readOnly: true }
      }
    : {};
  return (
    <TextField
      key={id}
      id={id}
      name={id}
      label={label || null}
      value={value}
      inputProps={{
        min: min !== undefined ? min : null,
        max: max !== undefined ? max : null
      }}
      multiline={multiline}
      rows={rows}
      variant="outlined"
      fullWidth
      required={required || false}
      margin="normal"
      type={subType === "number" ? "number" : null}
      onBlur={(e) => {
        if (max !== undefined && e.target.value > max) {
          e.target.value = max;
        } else if (min !== undefined && e.target.value < min) {
          e.target.value = min;
        }
        handleChange(e);
      }}
      onChange={(e) => {
        if (max !== undefined && e.target.value > max) {
          e.target.value = max;
        } else if (min !== undefined && e.target.value < min) {
          e.target.value = min;
        }
        handleChange(e);
      }}
      {...readOnlyProps}
      error={error}
      helperText={helperText}
    />
  );
}

function BCPFormRadio(props) {
  const {
    id,
    label,
    readonly,
    value,
    checked,
    handleChange,
    error,
    touched,
    textAlign
  } = props;
  return (
    <Box
      sx={{
        minHeight: 56,
        mt: 1,
        textAlign: textAlign
      }}
    >
      <FormControlLabel
        label={label}
        sx={{
          color: Boolean(touched && error) ? "#f44336" : "inherit",
          mr: label === undefined || label.trim() === "" ? 0 : "8px"
        }}
        control={
          <Checkbox
            name={id}
            value={value}
            checked={checked}
            error={error}
            onChange={handleChange}
            disabled={readonly}
          />
        }
      />
      {Boolean(touched && error) && (
        <FormHelperText error>{error}</FormHelperText>
      )}
    </Box>
  );
}

function BCPFormSelect(props) {
  const {
    id,
    label,
    value,
    handleChange,
    required,
    readonly,
    error,
    helperText,
    options
  } = props;

  const classes = useStyles();

  const formControlProps = {
    fullWidth: true,
    variant: "outlined",
    margin: "normal"
  };

  let selectProps = {
    native: true,
    value: value,
    onChange: handleChange,
    label: label
  };

  let inputProps = {
    name: id,
    id: id
  };
  if (required) {
    selectProps.required = required;
    inputProps.required = required;
  }

  if (readonly) {
    inputProps.disabled = true;
    inputProps.inputProps = { readOnly: true };
  }

  const CustomOption = ({ opt, index }) => {
    let val =
      typeof opt === "string" || opt instanceof String
        ? opt
        : opt.value || index;
    let label =
      typeof opt === "string" || opt instanceof String
        ? opt
        : opt.label || opt.value || index;
    let dataAttributes = {};
    if (opt.masterList) dataAttributes["data-masterlist"] = opt.masterList;
    if (opt.masterListIndex)
      dataAttributes["data-masterlistindex"] = opt.masterListIndex;
    return (
      <option key={index} value={val} {...dataAttributes}>
        {label}
      </option>
    );
  };

  let dense = false;

  return (
    <>
      <FormControl
        {...formControlProps}
        className={`${classes.formControl} ${
          readonly ? classes.formControlReadOnly : ""
        } ${dense ? classes.formControlDense : ""}`}
      >
        {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
        <Select {...selectProps} inputProps={{ ...inputProps }} error={error}>
          <option aria-label="None" value="" />
          {options &&
            options.map((opt, index) => {
              if (typeof opt == "object" && opt.group && opt.group !== "-1") {
                return (
                  <optgroup label={opt.group}>
                    {opt.options &&
                      opt.options.map((childOpt, childIndex) => {
                        return (
                          <CustomOption
                            key={childIndex}
                            index={childIndex}
                            opt={childOpt}
                          />
                        );
                      })}
                  </optgroup>
                );
              }
              return <CustomOption key={index} index={index} opt={opt} />;
            })}
        </Select>
      </FormControl>
      {error && <FormHelperText error>{helperText}</FormHelperText>}
    </>
  );
}

function setSelectOptions(options, col, clear) {
  let _options = clear ? [] : [...options];
  if (col.subType === "range") {
    let min = col.min || 0;
    let max = col.max || 100;
    for (let i = min; i <= max; i++) {
      _options.push(i);
    }
  } else if (col.subType === "list") {
    if (col.list) {
      col.list.forEach((item) => {
        let newOption;
        if (typeof item == "object") {
          if (item.name) {
            newOption = {
              label: item.name,
              value: item.name
            };
            if (item.value) {
              newOption.value = item.value;
            }
          } else if (item.value) {
            newOption = { value: item.value };
          }
        } else {
          newOption = { value: item };
        }
        if (newOption) _options.push(newOption);
      });
    }
  } else if (col.subType === "lists" || col.subType === "linkedLists") {
    if (col.lists && col.masterLists) {
      let optGroups = {};

      col.lists.forEach((listId) => {
        if (col.masterLists[listId]) {
          col.masterLists[listId].forEach((masterItem, index) => {
            let listItem = { ...masterItem };
            if (
              !listItem.group ||
              listItem.group === "" ||
              col.display === "single"
            ) {
              listItem.group = "-1";
            }
            if (!optGroups[listItem.group]) {
              optGroups[listItem.group] = [];
            }
            let newOption = {
              label: listItem.name,
              value: listItem.name,
              data: listItem,
              masterList: listId,
              masterListIndex: index
            };
            if (listItem.value) newOption.value = listItem.value;
            if (listItem.group) newOption.group = listItem.group;
            if (listItem.subGroup) newOption.subgroup = listItem.subGroup;

            optGroups[listItem.group].push(newOption);
          });
        }
      });

      Object.keys(optGroups).forEach((optGroupKey) => {
        if (optGroupKey === "-1") {
          _options = [..._options, ...optGroups[optGroupKey]];
        } else {
          let group = {
            group: optGroupKey,
            options: optGroups[optGroupKey]
          };
          _options.push(group);
        }
      });
    }
  }

  return _options;
}

function compGroupThenName(a, b) {
  const a1 = a.hasOwnProperty("group") ? a.group.toLowerCase() : "";
  const b1 = b.hasOwnProperty("group") ? b.group.toLowerCase() : "";
  const a2 = a.name.toLowerCase();
  const b2 = b.name.toLowerCase();

  return a1 < b1 ? -1 : a1 > b1 ? 1 : a2 < b2 ? -1 : a2 > b2 ? 1 : 0;
}

export function getPlayerName(player, def) {
  if (!player) {
    return def;
  } else if (
    (player?.firstName || player?.user?.firstName) &&
    (player?.lastName || player?.user?.lastName)
  ) {
    return (
      (player?.firstName || player?.user?.firstName) +
      " " +
      (player?.lastName || player?.user?.lastName)
    );
  } else if (player?.firstName || player?.user?.firstName) {
    return player?.firstName || player?.user?.firstName;
  } else if (player?.lastName || player?.user?.lastName) {
    return player?.lastName || player?.user?.lastName;
  } else {
    return def;
  }
}

export { flatternFormSchema };
